import { Pane, Text } from '@leverege/ui-elements'
import { GlobalState } from '@leverege/ui-redux'
import { curveStepAfter } from '@visx/curve'
import { LinearGradient } from '@visx/gradient'
import { ParentSize } from '@visx/responsive'
import {
  AnimatedAreaSeries,
  AnimatedAxis,
  DataProvider,
  Tooltip,
  XYChart,
} from '@visx/xychart'
import React, { useEffect, useState } from 'react'
import useOccupancyAreaChart from '../hooks/useOccupancyAreaChart'
import ChartLegend from './ChartLegend'
import S from './OccupancyAreaChart.css'
import { occupancyTheme } from './Theme'

function OccupancyAreaChart( props ) {
  const {
    events : { name, deviceId },
    delegate,
  } = props
  const { data, config } = useOccupancyAreaChart( props )

  return (
    <Pane variant="cardNoPad" layout="flex:col">
      <div
        style={{
          height : '60px',
          padding : 'var(--size-padLarge)',
          borderBottom : '1px solid var(--color-base100)',
        }}>
        <Text variant="subtitle">{name} Occupancy Over Time</Text>
      </div>
      <div
        style={{
          height : 'calc(100% - 60px)',
          padding :
            '0 var(--size-padLarge) var(--size-padLarge) var(--size-padLarge)',
        }}>
        <ParentSize
          parentSizeStyles={{
            overflow : 'hidden',
            width : '100%',
            height : '100%',
          }}>
          {( { width, height } ) => (
            <DataProvider
              theme={occupancyTheme}
              xScale={config.scale.x}
              yScale={config.scale.y}>
              <XYChart width={width} height={height - 20}>
                <LinearGradient
                  id="personGradient"
                  from={occupancyTheme.colors[0]}
                  fromOpacity={0.5}
                  toOpacity={0}
                  to={occupancyTheme.colors[0]}/>
                <LinearGradient
                  id="dogGradient"
                  from={occupancyTheme.colors[1]}
                  fromOpacity={0.5}
                  toOpacity={0}
                  to={occupancyTheme.colors[1]}/>
                <AnimatedAxis
                  orientation="left"
                  numTicks={6}
                  tickFormat={config.tickFormat.y}
                  label="Occupancy"/>
                <AnimatedAxis
                  tickFormat={config.tickFormat.x}
                  orientation="bottom"
                  numTicks={data.length}/>
                <AnimatedAreaSeries
                  fillOpacity={0.5}
                  fill="url(#personGradient)"
                  renderLine
                  dataKey="person"
                  data={data}
                  curve={curveStepAfter}
                  {...config.accessors.person}/>
                <AnimatedAreaSeries
                  fillOpacity={0.5}
                  fill="url(#dogGradient)"
                  renderLine
                  dataKey="dog"
                  data={data}
                  curve={curveStepAfter}
                  {...config.accessors.dog}/>
                <Tooltip
                  scroll
                  detectBounds
                  showSeriesGlyphs
                  showVerticalCrosshair
                  showDatumGlyph={false}
                  snapTooltipToDatumX={false}
                  snapTooltipToDatumY={false}
                  renderTooltip={props => (
                    <RenderTooltip
                      config={config}
                      delegate={delegate}
                      deviceId={deviceId}
                      {...props}/>
                  )}/>
                )
              </XYChart>
              <ChartLegend />
            </DataProvider>
          )}
        </ParentSize>
      </div>
    </Pane>
  )
}

function RoomImage( { src : pSrc, delegate, deviceId } ) {
  const [ src, setSrc ] = useState()
  const [ loading, setLoading ] = useState( false )

  useEffect( () => {
    if ( !deviceId || !pSrc ) {
      return
    }
    const rsrc = delegate.item( deviceId ).resource( pSrc ).getRsrc( GlobalState.get() )

    if ( rsrc ) {
      const url = new URL( rsrc.data.url )
      const expires = url.searchParams.get( 'Expires' )
      const expiresDate = new Date( parseInt( expires ) * 1000 )

      if ( expiresDate > new Date() ) {
        if ( rsrc.data.url !== src ) {
          setSrc( rsrc.data.url )
        }
        return
      }
    }

    setLoading( true )
    GlobalState
      .dispatch( delegate.item( deviceId ).resource( pSrc ).get() )
      .then( ( res ) => {
        setSrc( res.data.url )
        setLoading( false )
        return res
      } )
  }, [ delegate, src, pSrc, deviceId ] )

  if ( loading || !pSrc || !src ) {
    return (
      <div
        className={S.image}>
        {loading ? (
          <i
            className="fa fa-fw fa-spin fa-spinner fa-3x"
            style={{
              color : 'var(--color-brand500)',
            }}/>
        ) : (
          <Text variant="caption">No Image Available</Text>
        )}
      </div>
    )
  }

  return (
    <img
      className={S.image}
      src={encodeURI( src )}
      alt={src} />
  )
}

function RenderTooltip( { tooltipData, config, delegate, deviceId } ) {
  const formattedTime = ( value ) => {
    const int = parseInt( value )

    if ( int === 12 ) {
      return `${int} PM`
    }
    if ( int < 12 ) {
      return `${int} AM`
    }

    return `${int - 12} PM`
  }

  return (
    <div
      style={{
        display : 'flex',
        padding : 8,
        flexFlow : 'column wrap',
        gap : 20,
        fontSize : 16,
      }}>
      <div>{formattedTime(
        config.accessors.dog.xAccessor( tooltipData.nearestDatum.datum ),
      )}
      </div>
      <div
        style={{
          display : 'flex',
          flexFlow : 'column wrap',
          gap : 8,
          fontSize : 20,
        }}>
        <small
          style={{
            fontSize : 16,
            fontWeight : 300,
          }}>
          Occupancy
        </small>
        <div style={{ display : 'flex', alignItems : 'center', gap : 8 }}>
          <i className="fa-solid fa-dog" style={{ color : '#67798E' }} />
          {config.accessors.dog.yAccessor( tooltipData.nearestDatum.datum )}
          <i className="fa-solid fa-user-group" style={{ color : '#67798E' }} />
          {config.accessors.person.yAccessor( tooltipData.nearestDatum.datum )}
        </div>
        <RoomImage
          src={tooltipData.nearestDatum.datum?.imgSrc}
          delegate={delegate}
          deviceId={tooltipData.nearestDatum.datum?.imgDeviceId}/>
        <small>Last Updated: {formattedTime(
          config.accessors.dog.xAccessor( tooltipData.nearestDatum.datum ),
        )}
        </small>
      </div>
    </div>
  )
}

export default OccupancyAreaChart
